import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { Search, Sparkles, Loader2, Globe, Star } from 'lucide-react';
import { toast } from 'sonner';

const SOURCE = {
  regen: { label: 'YourReGen', color: '#65A30D', Icon: Star },
  fatsecret: { label: 'Global', color: '#00C8FF', Icon: Globe },
  ai: { label: 'Estimate', color: '#FFB800', Icon: Sparkles },
};

// Server-side food search: YourReGen foods first, FatSecret global, GPT-4o fallback.
export function SmartFoodSearch({ value, onPick, placeholder = 'Search any food…', testid }) {
  const [q, setQ] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setQ(value || ''); }, [value]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    const ctrl = new AbortController();
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/foods/search', { params: { q: term }, signal: ctrl.signal });
        setResults(data.results || []);
      } catch (e) { if (e.name !== 'CanceledError') setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => { clearTimeout(id); ctrl.abort(); };
  }, [q]);

  const pick = (food) => { setQ(food.name); onPick(food); setOpen(false); };

  const estimateAI = async () => {
    const term = q.trim();
    if (term.length < 2) return;
    setAiBusy(true);
    try {
      const { data } = await api.get('/foods/ai-estimate', { params: { q: term } });
      pick(data);
      toast.success(`Estimated "${data.name}"`);
    } catch { toast.error('Could not estimate this food'); }
    finally { setAiBusy(false); }
  };

  const showAiFallback = q.trim().length >= 2 && !loading && results.length === 0;

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />}
        <Input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); onPick({ name: e.target.value }); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          data-testid={testid}
          className="bg-[var(--c-film)] border-[var(--c-hair)] h-12 rounded-xl pl-9"
        />
      </div>
      {open && (results.length > 0 || showAiFallback) && (
        <div className="absolute z-30 mt-1 w-full rounded-xl bg-[var(--c-elev)] border border-[var(--c-hair)] shadow-xl max-h-72 overflow-y-auto" data-testid={`${testid}-results`}>
          {results.map((f, i) => {
            const s = SOURCE[f.source] || SOURCE.regen; const Ic = s.Icon;
            return (
              <button key={(f.id || f.name) + i} type="button" onClick={() => pick(f)}
                data-testid={`${testid}-opt-${i}`}
                className="w-full text-left px-4 py-2.5 hover:bg-[var(--c-film)] flex items-center gap-2 border-b border-[var(--c-hair)] last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{f.name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{Math.round(f.calories_per_100g)} kcal · P{Math.round(f.protein_per_100g)} C{Math.round(f.carbs_per_100g)} F{Math.round(f.fat_per_100g)} /100g</div>
                </div>
                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold flex-shrink-0" style={{ background: `${s.color}22`, color: s.color }}>
                  <Ic className="w-2.5 h-2.5" /> {s.label}
                </span>
              </button>
            );
          })}
          {showAiFallback && (
            <button type="button" onClick={estimateAI} disabled={aiBusy} data-testid={`${testid}-ai-estimate`}
              className="w-full text-left px-4 py-3 hover:bg-[#FFB800]/10 flex items-center gap-2 text-sm text-[#FFB800] font-medium">
              {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {aiBusy ? 'Estimating…' : `Estimate "${q.trim()}"`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
